import {
    Trophy,
    FileText,
    LogOut,
    Star,
    Flame,
    Building2,
    Volume2,
    VolumeX,
    X,
    History,
    CalendarDays,
    ListChecks,
    ListTodo,
    MessageSquare,
    Gauge,
    Archive,
    Network,
    Settings,
    ChevronDown,
    BarChart3,
    Puzzle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import { useMemberships } from '../hooks/useMemberships'
import { usePontaj } from '../hooks/usePontaj'
import IconButton from './ui/IconButton'
import CompanySwitcher from './CompanySwitcher'
import LanguageSwitcher from './LanguageSwitcher'

function getInitials(name) {
    if (!name) return '?'
    return name.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)
}

export default function Sidebar({
    onShowLeaderboard,
    showLeaderboard,
    onShowKpis,
    showKpis,
    onShowProcedures,
    showProcedures,
    onShowDepartments,
    showDepartments,
    onShowHistory,
    showHistory,
    onShowCalendar,
    showCalendar,
    onShowHome,
    showHome,
    onShowTasks,
    showTasks,
    onShowSuggestions,
    showSuggestions,
    onShowCockpit,
    showCockpit,
    onShowArchive,
    showArchive,
    onShowChromeExtension,
    showChromeExtension,
    onShowProfile,
    onShowCompany,
    onCloseMobile,
    onHideDesktop,
    dashboardV2Enabled = false,
    homeDomainCounts = {
        procedures: { todo: 0, claimed: 0, completed: 0 },
        tasks: { todo: 0, claimed: 0, completed: 0 },
    },
}) {
    const { user, profile, isAdmin, signOut } = useAuth()
    const { soundsOn, toggleSounds } = useSettings()
    const { t } = useTranslation()
    // Pontaj is now READ-ONLY in Kanbify — Pontify (Anviz) is the single source
    // of truth. The clock_in/clock_out RPCs still exist server-side and are used
    // by the pontify-clock-in webhook. No manual UI controls here.
    const { isActive, elapsed } = usePontaj(user?.id)
    const activeCompanyId = profile?.active_company_id || profile?.company_id
    const { memberships } = useMemberships(user?.id)
    const activeMembership = memberships.find((membership) => membership.company_id === activeCompanyId) || null
    const activeRole = activeMembership?.role || profile?.role
    const activeCompanyName = activeMembership?.companies?.name || profile?.company_name || t('company.yourCompany')
    const activeDisplayName = profile?.full_name || profile?.email

    const levelPct = () => {
        if (!profile) return 0
        const level = profile.level || 1
        const points = profile.points || 0
        const nextLevel = level * 100
        const previousLevel = (level - 1) * 100

        return Math.min(100, Math.round(((points - previousLevel) / (nextLevel - previousLevel)) * 100))
    }

    return (
        <aside className="sidebar" aria-label={t('sidebar.aria.mainNav')}>
            {onCloseMobile ? (
                <div className="sidebar-mobile-head">
                    <IconButton
                        icon={X}
                        label={t('sidebar.closeMenu')}
                        className="sidebar-mobile-close"
                        onClick={onCloseMobile}
                    />
                </div>
            ) : null}

            {onHideDesktop ? (
                <div className="sidebar-desktop-company-row">
                    <CompanySwitcher
                        activeCompanyId={profile?.active_company_id || profile?.company_id}
                        onCompanyChanged={() => window.location.reload()}
                    />
                    <IconButton
                        icon={X}
                        label={t('sidebar.closeMenu')}
                        className="sidebar-desktop-hide"
                        onClick={onHideDesktop}
                    />
                </div>
            ) : (
                <CompanySwitcher
                    activeCompanyId={profile?.active_company_id || profile?.company_id}
                    onCompanyChanged={() => window.location.reload()}
                />
            )}

            <div className="sidebar-scroll">
                {profile?.company_id && (
                    <div className="sidebar-pontaj">
                        {isActive ? (
                            <div
                                className="sidebar-pontaj-active sidebar-pontaj-readonly"
                                title={t('sidebar.pontaj.syncedTitle')}
                            >
                                <div className="sidebar-pontaj-info">
                                    <span className="sidebar-pontaj-dot" />
                                    <span className="sidebar-pontaj-label">{t('sidebar.pontaj.active')}</span>
                                    <span className="sidebar-pontaj-time">{elapsed}</span>
                                </div>
                                <span className="sidebar-pontaj-source">{t('sidebar.pontaj.source')}</span>
                            </div>
                        ) : (
                            <div
                                className="sidebar-pontaj-inactive"
                                title={t('sidebar.pontaj.inactiveTitle')}
                            >
                                <span className="sidebar-pontaj-dot sidebar-pontaj-dot-off" />
                                <div>
                                    <div className="sidebar-pontaj-label">{t('sidebar.pontaj.inactive')}</div>
                                    <div className="sidebar-pontaj-hint">{t('sidebar.pontaj.hint')}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <nav className="sidebar-nav">
                    {dashboardV2Enabled && onShowHome ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showHome ? 'active' : ''}`}
                            onClick={onShowHome}
                            aria-current={showHome ? 'page' : undefined}
                            title={t('sidebar.nav.homeTitle')}
                        >
                            <ListChecks size={18} />
                            <span className="sidebar-nav-label">{t('dashboard.domainTabs.procedures')}</span>
                            <span
                                className="sidebar-home-counters"
                                aria-label={t('sidebar.homeCounters.summary', {
                                    todo: homeDomainCounts.procedures.todo,
                                    claimed: homeDomainCounts.procedures.claimed,
                                    completed: homeDomainCounts.procedures.completed,
                                })}
                            >
                                <span
                                    className="sidebar-home-counter is-todo"
                                    title={t('sidebar.homeCounters.todo', { count: homeDomainCounts.procedures.todo })}
                                >
                                    {homeDomainCounts.procedures.todo}
                                </span>
                                <span
                                    className="sidebar-home-counter is-claimed"
                                    title={t('sidebar.homeCounters.claimed', { count: homeDomainCounts.procedures.claimed })}
                                >
                                    {homeDomainCounts.procedures.claimed}
                                </span>
                                <span
                                    className="sidebar-home-counter is-completed"
                                    title={t('sidebar.homeCounters.completed', { count: homeDomainCounts.procedures.completed })}
                                >
                                    {homeDomainCounts.procedures.completed}
                                </span>
                            </span>
                        </button>
                    ) : null}

                    {dashboardV2Enabled && onShowTasks ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showTasks ? 'active' : ''}`}
                            onClick={onShowTasks}
                            aria-current={showTasks ? 'page' : undefined}
                            title={t('dashboard.domainTabs.tasks')}
                        >
                            <ListTodo size={18} />
                            <span className="sidebar-nav-label">{t('dashboard.domainTabs.tasks')}</span>
                            <span
                                className="sidebar-home-counters"
                                aria-label={t('sidebar.homeCounters.summary', {
                                    todo: homeDomainCounts.tasks.todo,
                                    claimed: homeDomainCounts.tasks.claimed,
                                    completed: homeDomainCounts.tasks.completed,
                                })}
                            >
                                <span
                                    className="sidebar-home-counter is-todo"
                                    title={t('sidebar.homeCounters.todo', { count: homeDomainCounts.tasks.todo })}
                                >
                                    {homeDomainCounts.tasks.todo}
                                </span>
                                <span
                                    className="sidebar-home-counter is-claimed"
                                    title={t('sidebar.homeCounters.claimed', { count: homeDomainCounts.tasks.claimed })}
                                >
                                    {homeDomainCounts.tasks.claimed}
                                </span>
                                <span
                                    className="sidebar-home-counter is-completed"
                                    title={t('sidebar.homeCounters.completed', { count: homeDomainCounts.tasks.completed })}
                                >
                                    {homeDomainCounts.tasks.completed}
                                </span>
                            </span>
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className={`sidebar-nav-item ${showLeaderboard ? 'active' : ''}`}
                        onClick={onShowLeaderboard}
                        aria-current={showLeaderboard ? 'page' : undefined}
                        title={t('sidebar.nav.leaderboardTitle')}
                    >
                        <Trophy size={18} />
                        {t('nav.leaderboard')}
                    </button>

                    {profile?.company_id && onShowKpis ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showKpis ? 'active' : ''}`}
                            onClick={onShowKpis}
                            aria-current={showKpis ? 'page' : undefined}
                            title={t('sidebar.nav.kpisTitle')}
                        >
                            <BarChart3 size={18} />
                            {t('nav.kpis')}
                        </button>
                    ) : null}

                    <div className="sidebar-secondary-group">
                        {profile?.company_id && onShowProcedures ? (
                            <button
                                type="button"
                                className={`sidebar-nav-item ${showProcedures ? 'active' : ''}`}
                                onClick={onShowProcedures}
                                aria-current={showProcedures ? 'page' : undefined}
                                title={t('sidebar.nav.proceduresTitle')}
                            >
                                <FileText size={18} />
                                {t('nav.procedures')}
                            </button>
                        ) : null}

                        {profile?.company_id && onShowHistory ? (
                            <button
                                type="button"
                                className={`sidebar-nav-item ${showHistory ? 'active' : ''}`}
                                onClick={onShowHistory}
                                aria-current={showHistory ? 'page' : undefined}
                                title={t('sidebar.nav.historyTitle')}
                            >
                                <History size={18} />
                                {t('nav.history')}
                            </button>
                        ) : null}

                        {profile?.company_id && onShowCalendar ? (
                            <button
                                type="button"
                                className={`sidebar-nav-item ${showCalendar ? 'active' : ''}`}
                                onClick={onShowCalendar}
                                aria-current={showCalendar ? 'page' : undefined}
                                title={t('sidebar.nav.calendarTitle')}
                            >
                                <CalendarDays size={18} />
                                {t('nav.calendar')}
                            </button>
                        ) : null}

                        {onShowSuggestions ? (
                            <button
                                type="button"
                                className={`sidebar-nav-item ${showSuggestions ? 'active' : ''}`}
                                onClick={onShowSuggestions}
                                aria-current={showSuggestions ? 'page' : undefined}
                                title={t('sidebar.nav.suggestionsTitle')}
                            >
                                <MessageSquare size={18} />
                                {t('nav.suggestions')}
                            </button>
                        ) : null}
                    </div>

                    <details
                        className="sidebar-settings-group"
                    >
                        <summary className="sidebar-settings-heading">
                            <Settings size={14} />
                            <span>{t('common.settings')}</span>
                            <ChevronDown size={14} className="sidebar-settings-chevron" />
                        </summary>

                        <div
                            className="sidebar-settings-items"
                            role="group"
                            aria-label={t('common.settings')}
                        >
                            <LanguageSwitcher variant="nav" />

                            {profile?.company_id && onShowDepartments ? (
                                <button
                                    type="button"
                                    className={`sidebar-nav-item ${showDepartments ? 'active' : ''}`}
                                    onClick={onShowDepartments}
                                    aria-current={showDepartments ? 'page' : undefined}
                                    title={t('sidebar.nav.departmentsTitle')}
                                >
                                    <Network size={18} />
                                    {t('nav.departments')}
                                </button>
                            ) : null}

                            <button
                                type="button"
                                className="sidebar-nav-item"
                                onClick={toggleSounds}
                                aria-pressed={soundsOn}
                                title={soundsOn ? t('sidebar.sounds.onTitle') : t('sidebar.sounds.offTitle')}
                            >
                                {soundsOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
                                {soundsOn ? t('nav.soundsOn') : t('nav.soundsOff')}
                            </button>

                            {onShowCompany && profile?.company_id ? (
                                <button type="button" className="sidebar-nav-item" onClick={onShowCompany}
                                    title={isAdmin ? t('sidebar.company.adminTitle') : t('sidebar.company.employeeTitle')}
                                >
                                    <Building2 size={18} />
                                    {isAdmin ? t('sidebar.company.adminLabel') : t('sidebar.company.employeeLabel')}
                                </button>
                            ) : null}

                            {profile?.company_id && onShowArchive ? (
                                <button
                                    type="button"
                                    className={`sidebar-nav-item ${showArchive ? 'active' : ''}`}
                                    onClick={onShowArchive}
                                    aria-current={showArchive ? 'page' : undefined}
                                    title={t('sidebar.nav.archiveTitle')}
                                >
                                    <Archive size={18} />
                                    {t('nav.archive')}
                                </button>
                            ) : null}

                            {onShowChromeExtension ? (
                                <button
                                    type="button"
                                    className={`sidebar-nav-item ${showChromeExtension ? 'active' : ''}`}
                                    onClick={onShowChromeExtension}
                                    aria-current={showChromeExtension ? 'page' : undefined}
                                    title="Instalează extensia Kanbify pentru Chrome"
                                >
                                    <Puzzle size={18} />
                                    Extensie Chrome
                                </button>
                            ) : null}

                            {onShowCockpit && (isAdmin || profile?.role === 'manager') ? (
                                <button
                                    type="button"
                                    className={`sidebar-nav-item ${showCockpit ? 'active' : ''}`}
                                    onClick={onShowCockpit}
                                    aria-current={showCockpit ? 'page' : undefined}
                                    title={t('sidebar.nav.cockpitTitle')}
                                >
                                    <Gauge size={18} />
                                    {t('nav.cockpit')}
                                </button>
                            ) : null}
                        </div>
                    </details>
                </nav>

            </div>

            <div className="sidebar-user-section">
                {profile ? (
                    <>
                        <div className="sidebar-level-row">
                            <span className="sidebar-level-label">{t('sidebar.levelShort', { level: profile.level || 1 })}</span>
                            <div className="sidebar-level-bar">
                                <div className="sidebar-level-fill" style={{ width: `${levelPct()}%` }} />
                            </div>
                            <span className="sidebar-points">
                                <Star size={10} />
                                {profile.points || 0}
                            </span>
                        </div>
                        {(profile.streak_days || 0) > 1 ? (
                            <div className="sidebar-streak">
                                <Flame size={12} />
                                {t('sidebar.streak', { count: profile.streak_days })}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="sidebar-profile-warning">{t('sidebar.profileUnavailable')}</div>
                )}

                <div className="sidebar-user">
                    <button
                        type="button"
                        className="sidebar-user-trigger"
                        onClick={profile ? onShowProfile : undefined}
                        title={t('sidebar.profile.openTitle')}
                    >
                        <div
                            className="sidebar-user-avatar"
                            style={{
                                background: profile
                                    ? `hsl(${(profile.full_name?.length || 0) * 37 % 360}, 50%, 40%)`
                                    : 'var(--accent)',
                            }}
                        >
                            {getInitials(activeDisplayName)}
                        </div>
                        <div className="sidebar-user-info">
                            <div className="sidebar-user-name">{activeDisplayName || t('sidebar.noProfile')}</div>
                            <div className="sidebar-user-status">
                                {profile ? t('sidebar.profile.roleInCompany', {
                                    role: t(activeRole === 'admin' ? 'company.roleAdmin' : activeRole === 'manager' ? 'company.roleManager' : 'company.roleEmployee'),
                                    company: activeCompanyName,
                                }) : t('sidebar.sessionActive')}
                            </div>
                        </div>
                    </button>

                    <IconButton icon={LogOut} label={t('common.logout')} className="sidebar-logout" onClick={signOut} />
                </div>
            </div>
        </aside>
    )
}
